import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import PasswordStrengthIndicator from '../components/common/PasswordStrengthIndicator';
import { createUser, validateInvitationToken } from '../services/api';
import { validatePassword } from '../utils/passwordValidation';

interface SharepointConfig {
  tenantId: string;
  clientId: string;
  clientSecret: string;
  siteId: string;
  driveId: string;
  basePath: string;
}

interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
      errors?: Record<string, string[]>;
      passwordErrors?: string[];
    };
  };
}

// SharepointFieldName is imported as a type above for validator usage

interface PasswordValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  strength: number;
}

interface UserData {
  displayName: string;
  clientName: string;
  email: string;
  phoneNumber: string;
  password: string;
  storageType: string;
  billingType: string;
  timezone: string;
  sharepointConfig?: SharepointConfig;
  termsAccepted: boolean;
  userType: string;
  invitationCode?: string;
  inviteToken?: string;

  publicKey?: JsonWebKey;
}

const getFieldError = (name: keyof UserData, value: string): string | undefined => {
  if (!value) {
    return 'This field is required.';
  }
  if (name === 'email' && !/\S+@\S+\.\S+/.test(value)) {
    return 'Invalid email address.';
  }
  if (name === 'displayName' && value.length < 2) {
    return 'Full name must be at least 2 characters long.';
  }
  if (name === 'phoneNumber' && !/^\+[1-9]\d{1,14}$/.test(value)) {
    return 'Invalid phone number format.';
  }
  return undefined;
};

const SignupPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [displayName, setDisplayName] = useState<string>('');
  const [clientName, setClientName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckingInvite, setIsCheckingInvite] = useState<boolean>(false);
  const [isInvitation, setIsInvitation] = useState<boolean>(false);
  const [inviteToken, setInviteToken] = useState<string>('');

  // Recovery Code State

  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    isValid: false,
    errors: [],
    warnings: [],
    strength: 0,
  });

  const [userType, _setUserType] = useState('individual');
  const [invitationCode, _setInvitationCode] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsScrolled, setIsTermsScrolled] = useState(false);
  const [termsText, setTermsText] = useState<string>('Loading...');

  const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof UserData, string>>>(
    {}
  );
  const [apiValidationErrors, setApiValidationErrors] = useState<
    Record<string, string[] | undefined>
  >({});

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setIsCheckingInvite(true);
      setInviteToken(token);
      validateInvitationToken(token)
        .then((response) => {
          if (response.success && response.valid) {
            setIsInvitation(true);
            if (response.email) {
              setEmail(response.email);
            }
            if (response.clientName) {
              setClientName(response.clientName);
            }
            // Invited users join an existing client, so they are effectively "individual" in terms of signup flow (no billing setup etc. yet)
            // But strict "userType" might be better handled by backend based on token.
            // For now, we'll keep 'individual' as they aren't creating a NEW business account.
            _setUserType('individual');
          } else {
            setError(response.message || 'Invalid or expired invitation link.');
          }
        })
        .catch((_err) => {
          setError('Failed to validate invitation.');
        })
        .finally(() => {
          setIsCheckingInvite(false);
        });
    }
  }, [searchParams]);

  useEffect(() => {
    fetch('/termsAndConditions.md')
      .then((response) => response.text())
      .then((text) => setTermsText(text))
      .catch(() => setTermsText('Failed to load terms and conditions.'));
  }, []);

  React.useEffect(() => {
    const userInfo = {
      email,
      displayName,
      clientName,
    };
    const validation = validatePassword(password, userInfo);
    setPasswordValidation(validation);
  }, [password, email, displayName, clientName]);
  const validateField = (name: keyof UserData, value: string) => {
    const error = getFieldError(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleTermsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    // Add a small buffer to account for floating point inaccuracies
    if (scrollHeight - scrollTop <= clientHeight + 1) {
      setIsTermsScrolled(true);
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!termsAccepted) {
      setError('You must accept the Terms and Conditions to create an account.');
      return;
    }

    setIsLoading(true);

    const trimmedDisplayName = displayName.trim();
    const trimmedClientName = clientName.trim();
    const trimmedEmail = email.trim();
    const normalizedPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');
    const trimmedInvitationCode = invitationCode.trim();

    if (
      !trimmedDisplayName ||
      !trimmedClientName ||
      !trimmedEmail ||
      !password ||
      !normalizedPhoneNumber ||
      (userType === 'business' && !trimmedInvitationCode)
    ) {
      setError('All fields are required.');
      setIsLoading(false);
      return;
    }

    if (!passwordValidation.isValid) {
      if (
        passwordValidation.errors.length === 1 &&
        passwordValidation.errors[0]?.includes('personal information')
      ) {
        setError(
          'Password cannot contain personal information like your name, company name, or email.'
        );
      } else {
        setError(
          'Password does not meet security requirements. Please check the requirements below.'
        );
      }
      setIsLoading(false);
      return;
    }

    try {
      const userData: UserData = {
        displayName: trimmedDisplayName,
        clientName: trimmedClientName,
        email: trimmedEmail,
        phoneNumber: normalizedPhoneNumber,
        password,
        storageType: 'firebase',
        billingType: 'self-managed',
        timezone: 'UTC',
        termsAccepted,
        userType,
        ...(inviteToken ? { inviteToken } : {}),
      };

      if (userType === 'business') {
        userData.invitationCode = trimmedInvitationCode;
      }

      const response = await createUser(userData);

      if (response.success) {
        setSuccess('Account created successfully!');
        setIsLoading(false);
        navigate('/login', { state: { email: trimmedEmail } });
      } else {
        setError(response.message || 'An unexpected error occurred.');
        setIsLoading(false);
      }
    } catch (err: unknown) {
      const error = err as ApiError;

      if (error.response?.data?.passwordErrors && error.response.data.passwordErrors.length > 0) {
        setError(
          error.response.data.passwordErrors[0] || 'Password does not meet security requirements.'
        );
      } else if (error.response?.data?.errors) {
        setApiValidationErrors(error.response.data.errors);
        setError('Please correct the errors in the form.');
      } else {
        const errorMessage =
          error.response?.data?.message || error.message || 'An unexpected error occurred.';

        if (errorMessage.includes('Email already exists')) {
          setError(
            'An account with this email address already exists. Please use a different email or try logging in.'
          );
        } else if (errorMessage.includes('Phone number already in use')) {
          setError(
            'This phone number is already registered. Please use a different number or log in.'
          );
        } else {
          setError(errorMessage);
        }
      }
      setIsLoading(false);
    }
  };

  const handleDisplayNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setDisplayName(event.target.value);
  };

  const handleClientNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setClientName(event.target.value);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value);
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // Strip spaces, dashes, and parentheses immediately
    const cleaned = event.target.value.replace(/[^\d+]/g, '');
    setPhoneNumber(cleaned);
  };

  const handleDisplayNameBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setDisplayName(value.trim());
    validateField(name as keyof UserData, value.trim());
  };

  const handleClientNameBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setClientName(value.trim());
  };

  const handleEmailBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setEmail(value.trim());
    validateField(name as keyof UserData, value.trim());
  };

  const handlePhoneNumberBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    // Value is already cleaned by onChange
    validateField(name as keyof UserData, value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.target.value);
  };

  const isButtonDisabled =
    isLoading ||
    !!success ||
    (!!password && !passwordValidation.isValid) ||
    !termsAccepted ||
    !displayName ||
    !clientName ||
    !email ||
    !phoneNumber ||
    Object.values(validationErrors).some((error) => !!error) ||
    (userType === 'business' && !invitationCode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        paddingTop: '20px',
        paddingBottom: '20px',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'left',
          }}
        >
          <img
            src="/Bidlogiq_Logo-Long_Full colours.png"
            alt="BIDLOGIQ Logo"
            style={{ maxWidth: '300px', marginBottom: '24px' }}
          />
          {isCheckingInvite ? (
            <CircularProgress />
          ) : !isInvitation ? (
            <Box
              sx={{
                mb: 3,
                p: 2,
                border: '1px dashed',
                borderColor: 'grey.400',
                borderRadius: 1,
                width: '100%',
                textAlign: 'center',
              }}
            >
              {error ? (
                <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                  {error}
                </Alert>
              ) : null}
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Registration is Invite-Only
              </Typography>
              <Typography variant="body1" color="text.primary" paragraph>
                Self-serve registration is currently disabled. Please contact our sales team to
                request an invitation and set up your organization's workspace.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                href="mailto:sales@bidlogiq.ai"
                sx={{ mt: 2 }}
              >
                Contact Sales
              </Button>
              <Box sx={{ mt: 3 }}>
                <RouterLink to="/login" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary">
                    Already have an account? Log in
                  </Typography>
                </RouterLink>
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="body1" color="text.primary" gutterBottom>
                {`You have been invited to join ${clientName}. Please complete your registration.`}
              </Typography>
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="displayName"
                  label="Full Name"
                  name="displayName"
                  autoComplete="name"
                  autoFocus
                  value={displayName}
                  onChange={handleDisplayNameChange}
                  onBlur={handleDisplayNameBlur}
                  disabled={isLoading}
                  error={!!validationErrors.displayName || displayName.length > 50}
                  inputProps={{ maxLength: 50 }}
                  helperText={
                    validationErrors.displayName ||
                    apiValidationErrors.displayName?.[0] ||
                    `${displayName.length}/50`
                  }
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="clientName"
                  label="Company Name"
                  name="clientName"
                  autoComplete="organization"
                  value={clientName}
                  onChange={handleClientNameChange}
                  onBlur={handleClientNameBlur}
                  disabled={isLoading || isInvitation} // Disable if invited
                  error={!!apiValidationErrors.clientName || clientName.length > 100}
                  inputProps={{ maxLength: 100 }}
                  helperText={apiValidationErrors.clientName?.[0] || `${clientName.length}/100`}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  disabled={isLoading || isInvitation} // Disable if invited
                  error={!!validationErrors.email}
                  helperText={validationErrors.email || apiValidationErrors.email?.[0]}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="phoneNumber"
                  label="Phone Number"
                  name="phoneNumber"
                  autoComplete="tel"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  onBlur={handlePhoneNumberBlur}
                  disabled={isLoading}
                  error={!!validationErrors.phoneNumber || !!apiValidationErrors.phoneNumber}
                  helperText={
                    validationErrors.phoneNumber ||
                    apiValidationErrors.phoneNumber?.[0] ||
                    'Enter phone number in international format (e.g., +61555555555).'
                  }
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={isLoading}
                  error={
                    !!password && (!passwordValidation.isValid || !!apiValidationErrors.password)
                  }
                  helperText={
                    password && !passwordValidation.isValid
                      ? 'Password does not meet requirements'
                      : apiValidationErrors.password?.[0]
                  }
                  data-testid="password-input"
                />

                <PasswordStrengthIndicator
                  password={password}
                  userInfo={{ email, displayName, clientName }}
                  showRequirements={true}
                  showStrengthMeter={true}
                  showValidationMessages={false}
                />

                <Box
                  sx={{
                    mt: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    height: '200px',
                    overflowY: 'auto',
                    backgroundColor: theme.palette.background.paper,
                    fontSize: '0.8rem',
                  }}
                  onScroll={handleTermsScroll}
                  data-testid="terms-box"
                >
                  <ReactMarkdown
                    components={{
                      h2: ({ children }) => (
                        <Typography component="h2" variant="caption" gutterBottom>
                          {children}
                        </Typography>
                      ),
                      h3: ({ children }) => (
                        <Typography component="h3" variant="caption" gutterBottom>
                          {children}
                        </Typography>
                      ),
                      p: ({ children }) => (
                        <Typography component="p" variant="body2" paragraph>
                          {children}
                        </Typography>
                      ),
                      li: ({ children }) => (
                        <li>
                          <Typography component="span" variant="body2">
                            {children}
                          </Typography>
                        </li>
                      ),
                    }}
                  >
                    {termsText}
                  </ReactMarkdown>
                </Box>
                {!isTermsScrolled && (
                  <Typography
                    variant="caption"
                    color="text.primary"
                    sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                  >
                    Please read through the terms and conditions. The checkbox will be enabled once
                    you reach the bottom.
                  </Typography>
                )}
                <FormControlLabel
                  data-testid="terms-checkbox-label"
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      disabled={!isTermsScrolled || isLoading}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      By confirming my acceptance, I acknowledge that I have read and agree to be
                      bound by{' '}
                      <RouterLink
                        to="/terms-conditions"
                        target="_blank"
                        rel="noopener"
                        style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
                      >
                        BIDLOGIQ’s Terms and Conditions.
                      </RouterLink>
                      .
                      {userType === 'business' &&
                        ' By confirming my acceptance, I also confirm I am authorised to accept these Terms and Conditions on behalf of the company referred to in this registration and bind that company to these Terms and Conditions.'}
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
                    {success}
                  </Alert>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, color: 'white', fontWeight: 'bold', fontSize: '1.2rem' }}
                  disabled={isButtonDisabled}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
                <Typography variant="body2" color="text.primary">
                  Already have an account?{' '}
                  <RouterLink
                    to="/login"
                    style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
                  >
                    Sign In
                  </RouterLink>
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default SignupPage;
